import { createContext, PropsWithChildren, useMemo } from 'react'

import { useSelector } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router'

import { IApplicationState } from '@store'
import { ICampaign, selectCampaign } from '@store/campaigns'
import { selectCampaignId, selectSubmissionId } from '@store/router'
import { ISubmission, selectSubmission } from '@store/submissions'
import {
  getCampaignProperties,
  getSubmissionLocationProps,
  getSubmissionProperties,
  toEventsTracking,
} from '@utils'

export interface IAnalyticsTrackingContextProps {
  getSubmissionTrackingProps: (submission?: ISubmission, campaign?: ICampaign) => object
  getSubmissionTrackingEvent: (eventName: string, submission: ISubmission) => object | undefined
  getCampaignTrackingProps: (campaign?: ICampaign) => object
  getCampaignTrackingEvent: (
    eventName: string,
    campaign: ICampaign,
    otherProps: object,
  ) => object | undefined
  getSubmissionLocationProps: () => object
}

export const AnalyticsTrackingContext = createContext({})

// Base component for testing
export const AnalyticsTrackingProvider: React.FC<
  PropsWithChildren<{
    campaign: ICampaign
    submission: ISubmission
    history: any
  }>
> = ({ children, campaign, submission, history }) => {
  const getSubmissionTrackingProps = useMemo(
    () =>
      (submissionArg: ISubmission = submission, campaignArg: ICampaign = campaign) => ({
        ...getCampaignProperties(campaignArg),
        ...getSubmissionProperties(submissionArg),
        ...getSubmissionLocationProps(history.location),
      }),
    [campaign, submission, history],
  )

  const getCampaignTrackingProps = useMemo(
    () =>
      (campaignArg: ICampaign = campaign) => ({
        ...getCampaignProperties(campaignArg),
        ...getSubmissionLocationProps(history.location),
      }),
    [campaign, history],
  )

  const getSubmissionTrackingEvent = useMemo(
    () =>
      (eventName: string, submissionArg: ISubmission = submission) => {
        return toEventsTracking(eventName, getSubmissionTrackingProps(submissionArg))
      },
    [submission, getSubmissionTrackingProps],
  )

  const getCampaignTrackingEvent = useMemo(
    () =>
      (eventName: string, campaignArg: ICampaign = campaign, otherProps: object = {}) => {
        return toEventsTracking(eventName, {
          ...getCampaignTrackingProps(campaignArg),
          ...otherProps,
        })
      },
    [campaign, getCampaignTrackingProps],
  )

  const getSubmissionLocationPropsFunc = useMemo(
    () => () => getSubmissionLocationProps(history.location),
    [history],
  )

  const contextProps: IAnalyticsTrackingContextProps = useMemo(
    () => ({
      getSubmissionTrackingProps,
      getSubmissionTrackingEvent,
      getCampaignTrackingProps,
      getCampaignTrackingEvent,
      getSubmissionLocationProps: getSubmissionLocationPropsFunc,
    }),
    [
      getSubmissionTrackingProps,
      getSubmissionTrackingEvent,
      getCampaignTrackingProps,
      getCampaignTrackingEvent,
      getSubmissionLocationPropsFunc,
    ],
  )

  return (
    <AnalyticsTrackingContext.Provider value={contextProps}>
      {children}
    </AnalyticsTrackingContext.Provider>
  )
}

// Connected component using hooks
const ConnectedAnalyticsTrackingProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const match = useRouteMatch()
  const history = useHistory()

  const campaignId = useSelector(() => selectCampaignId(match) as number)
  const submissionId = useSelector(() => selectSubmissionId(match) as number)
  const campaign = useSelector((state: IApplicationState) => selectCampaign(state, campaignId!))
  const submission = useSelector((state: IApplicationState) =>
    selectSubmission(state, submissionId!),
  )

  return (
    <AnalyticsTrackingProvider campaign={campaign} submission={submission} history={history}>
      {children}
    </AnalyticsTrackingProvider>
  )
}

export default ConnectedAnalyticsTrackingProvider
